import { AppStateSlice } from '$tablet-app/src/app/stores/app_state';
import { useStateStore } from '$tablet-app/src/app/stores/useStateStore';
import {
  CreateMealMutation,
  GraphQLResult,
  MealType,
} from '@akin-home/amplify';
import { getRandomItemFromList } from '@akin-home/arrays';
import { startOfWeek } from '@akin-home/dates';
import {
  createMealFromRecipe,
  getMealTypeTimes,
  groupMealsIntoWeekPlan,
} from '@akin-home/meals';
import { OptionType } from '@akin-home/options';
import { listAllOptionModels } from '@akin-home/options/frontend';
import { checkMealTypesToPlan } from '@akin-home/recipes';
import {
  getRecipeFilterFromMealPreferences,
  listAllRecipeModels,
} from '@akin-home/recipes-front-end';
import { addWeeks } from 'date-fns';
import { fetchRecommendedRecipes } from '../../mealPlan/recommendation';
import { MealPlanScreenState } from './types';

export const createMealPlanScreenState: AppStateSlice<MealPlanScreenState> = (
  set,
  get
) => ({
  meal_plan_screen: {
    week_start_date: startOfWeek(new Date()),
    regenerate_meal_plan_confirmation_visible: false,

    setWeek(date: Date) {
      set((state) => {
        state.meal_plan_screen.week_start_date = startOfWeek(date);
      });
    },

    goToNextWeek() {
      set((state) => {
        state.meal_plan_screen.week_start_date = startOfWeek(
          addWeeks(new Date(state.meal_plan_screen.week_start_date), 1)
        );
      });
    },

    goToPreviousWeek() {
      set((state) => {
        state.meal_plan_screen.week_start_date = startOfWeek(
          addWeeks(new Date(state.meal_plan_screen.week_start_date), -1)
        );
      });
    },

    askToRegenerateMealPlan() {
      set((state) => {
        state.meal_plan_screen.regenerate_meal_plan_confirmation_visible = true;
      });
    },

    declineRegenerateMealPlan() {
      set((state) => {
        state.meal_plan_screen.regenerate_meal_plan_confirmation_visible =
          false;
      });
    },

    async generateMealPlan(data, create) {
      const week_start_date = get().meal_plan_screen.week_start_date;
      const habitatID = get().current_user?.habitatID;

      if (!habitatID) return;

      set((state) => {
        state.meal_plan_screen.generating_meal_plan = true;
      });

      const preferences = await get().getUserMealPreferences();

      const allergies = await listAllOptionModels({
        filter: { type: { eq: OptionType.ALLERGY } },
      });

      const filter = getRecipeFilterFromMealPreferences({
        preferences,
        allergy_options: allergies,
      });

      const recipes = await listAllRecipeModels({
        filter: { ...filter, inProgress: { ne: true } },
      });

      const meals_to_plan = checkMealTypesToPlan(preferences);

      const recommended_recipes = fetchRecommendedRecipes(recipes, allergies, {
        ...preferences,
      });

      /** Fill plan with recipes */
      const current_meal_plan = groupMealsIntoWeekPlan({
        meals: data,
        week_start_date,
      });

      const create_requests: Promise<GraphQLResult<CreateMealMutation>>[] = [];

      /** Fill breakfasts */
      current_meal_plan.forEach((day) => {
        const date = new Date(day.date);

        if (meals_to_plan.Breakfast && !day.meals.Breakfast) {
          const recipe = getRandomItemFromList(recommended_recipes.Breakfast);

          const new_meal = createMealFromRecipe(recipe, {
            mealType: MealType.Breakfast,
            habitatID,
            ...getMealTypeTimes({
              meal_type: MealType.Breakfast,
              date,
              time: preferences.meal_times?.Breakfast,
            }),
          });
          create_requests.push(create(new_meal));
        }

        if (meals_to_plan.Lunch && !day.meals.Lunch) {
          const recipe = getRandomItemFromList(recommended_recipes.Lunch);

          const new_meal = createMealFromRecipe(recipe, {
            mealType: MealType.Lunch,
            habitatID,
            ...getMealTypeTimes({
              meal_type: MealType.Lunch,
              date,
              time: preferences.meal_times?.Lunch,
            }),
          });
          create_requests.push(create(new_meal));
        }

        if (meals_to_plan.Dinner && !day.meals.Dinner) {
          const recipe = getRandomItemFromList(recommended_recipes.Dinner);
          create_requests.push(
            create(
              createMealFromRecipe(recipe, {
                mealType: MealType.Dinner,
                habitatID,
                ...getMealTypeTimes({
                  meal_type: MealType.Dinner,
                  date,
                  time: preferences.meal_times?.Dinner,
                }),
              })
            )
          );
        }

        if (meals_to_plan.Snack && !day.meals.Snack) {
          const recipe = getRandomItemFromList(recommended_recipes.Snack);
          create_requests.push(
            create(
              createMealFromRecipe(recipe, {
                mealType: MealType.Snack,
                habitatID,
                ...getMealTypeTimes({
                  meal_type: MealType.Snack,
                  date,
                  time: preferences.meal_times?.Snack,
                }),
              })
            )
          );
        }
      });

      await Promise.all(create_requests);

      set((state) => {
        state.meal_plan_screen.generating_meal_plan = false;
      });
    },

    async regenerateMealPlan(data, create, deleteMeal) {
      set((state) => {
        state.meal_plan_screen.generating_meal_plan = true;
        state.meal_plan_screen.regenerate_meal_plan_confirmation_visible =
          false;
      });

      /** Delete the existing meals */
      await Promise.all(
        data.map((meal) => deleteMeal({ input: { id: meal.id } }))
      );

      /** Call generate with empty data */
      get().meal_plan_screen.generateMealPlan([], create);
    },
  },
});

export const useMealPlanScreenState = () =>
  useStateStore((state) => state.meal_plan_screen);
